import { IonIcon, IonSpinner } from '@ionic/react';
import { eyeOutline, refreshOutline } from 'ionicons/icons';
import React, { useCallback, useEffect, useState } from 'react';
import type { AutomationDryRunResult } from '../../automation/dryRun';
import { previewAutomationRun } from '../../services/Automations.service';
import type {
  AutomationGraph,
  AutomationTrigger,
} from '../../types/AutomationWorkflow.types';

export const AutomationDryRunPanel: React.FC<{
  trigger: AutomationTrigger;
  graph: AutomationGraph;
}> = ({ trigger, graph }) => {
  const [result, setResult] = useState<AutomationDryRunResult | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setResult(await previewAutomationRun({ trigger, graph }));
    } finally {
      setLoading(false);
    }
  }, [trigger, graph]);

  useEffect(() => {
    void refresh();
    // Reloading on every keystroke would re-parse metadata, so this is a
    // one-shot load with an explicit refresh control.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remaining = result ? result.matchCount - result.files.length : 0;

  return (
    <section className="automation-dry-run">
      <header className="automation-dry-run-head">
        <div>
          <p className="automation-dry-run-title">
            <IonIcon icon={eyeOutline} aria-hidden />
            Dry run preview
          </p>
          <p className="automation-dry-run-sub">
            Nothing is written until you hit Run.
          </p>
        </div>
        <button
          type="button"
          className="automation-link-button"
          disabled={loading}
          onClick={() => void refresh()}
        >
          <IonIcon icon={refreshOutline} aria-hidden />
          Refresh
        </button>
      </header>

      {loading && (
        <div className="automation-dry-run-loading">
          <IonSpinner name="crescent" />
        </div>
      )}

      {!loading && result?.notice && (
        <p className="automation-dry-run-notice">{result.notice}</p>
      )}

      {!loading && result && result.files.length > 0 && (
        <>
          <ul className="automation-dry-run-list">
            {result.files.map((file) => (
              <li key={file.filePath} className="automation-dry-run-row">
                <div className="automation-dry-run-file">
                  <span className="automation-dry-run-file-title">
                    {file.title}
                  </span>
                  {file.meta && (
                    <span className="automation-dry-run-file-meta">
                      {file.meta}
                    </span>
                  )}
                </div>
                <div className="automation-dry-run-ops">
                  {file.ops.map((op, index) => (
                    <span
                      key={`${op.nodeId}-${index}`}
                      className={`automation-dry-run-op tone-${op.tone}`}
                    >
                      {op.label}
                    </span>
                  ))}
                </div>
              </li>
            ))}
          </ul>
          {remaining > 0 && (
            <p className="automation-dry-run-more">
              + {remaining} more file{remaining === 1 ? '' : 's'} waiting in
              this folder.
            </p>
          )}
        </>
      )}
    </section>
  );
};
